import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

import { UserService } from '../../services/user.service';
import { ReferenceDataService } from '../../../features/reference-data/services/reference-data.service';
import { PinVaultService } from '../../services/pin-vault.service';
import { User } from '../../models/user.model';
import { environment } from '../../../../environments/environment';

export interface ProfileState {
  showProfileModal: boolean;
  profileEmail: string;
  profilePhone: string;
  profileAvatarUrl: string;
  profileUsername: string;
  profileContacts: { fullName: string; phoneNumber: string; relationshipId: string | number }[];
  parentalLinks: { id: number; label: string; code?: string }[];
  profileSaving: boolean;
  pmLangOpen: boolean;
  pmThemeOpen: boolean;
  userHasPin: boolean;
  currentUser: User | null;
}

export class TopbarProfileCollab {

  constructor(
    private userService: UserService,
    private referenceDataService: ReferenceDataService,
    private translate: TranslateService,
    private messageService: MessageService,
    private pinVaultService: PinVaultService,
  ) {}

  goToProfile(state: ProfileState, closeAll: () => void): void {
    closeAll();
    state.profileEmail = state.currentUser?.email || '';
    state.profilePhone = (state.currentUser as any)?.phoneNumber || '';
    state.profileAvatarUrl = state.currentUser?.avatarUrl || '';
    state.profileUsername = state.currentUser?.username || '';
    state.profileContacts = [];
    state.showProfileModal = true;

    // Check PIN vault status
    const userEmail = state.currentUser?.email || localStorage.getItem('currentUserEmail') || '';
    if (userEmail) {
      this.pinVaultService.hasVault(userEmail).then(has => state.userHasPin = has);
    }

    // Fetch full user from DB (contacts, phone, etc.)
    if (state.currentUser?.email) {
      this.userService.findByEmail(state.currentUser.email).subscribe({
        next: (dbUser) => {
          if (dbUser) {
            state.profilePhone = dbUser.phoneNumber || state.profilePhone;
            state.profileContacts = (dbUser.contacts || []).map((c: any) => ({ ...c }));
            state.profileUsername = dbUser.username || state.profileUsername;
            if (dbUser.avatarUrl) state.profileAvatarUrl = dbUser.avatarUrl;
          }
        },
        error: () => { /* keep defaults */ }
      });
    }

    // Load parental links for contact relationship dropdown
    if (state.parentalLinks.length === 0) {
      this.referenceDataService.getAll('parental-links').subscribe({
        next: (links) => { state.parentalLinks = links.filter((l: any) => l.isActive !== false); },
        error: () => { state.parentalLinks = []; }
      });
    }
  }

  closeProfileModal(state: ProfileState): void {
    state.showProfileModal = false;
    state.pmLangOpen = false;
    state.pmThemeOpen = false;
  }

  onProfilePhotoSelected(event: Event, state: ProfileState): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    if (file.size > environment.maxImageSizeMB * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMON.ERROR'),
        detail: this.translate.instant('MESSAGES.PROFILE.PHOTO_SIZE_ERROR')
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      state.profileAvatarUrl = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  saveProfileModal(state: ProfileState): void {
    if (!state.profileUsername) return;
    state.profileSaving = true;
    const validContacts = state.profileContacts.filter(c => c.fullName.trim() || c.phoneNumber.trim());
    this.userService.updateMyProfile({
      avatarUrl: state.profileAvatarUrl,
      email: state.profileEmail,
      phoneNumber: state.profilePhone,
      contacts: validContacts
    }).subscribe({
      next: () => {
        if (state.currentUser) {
          state.currentUser = {
            ...state.currentUser,
            email: state.profileEmail,
            avatarUrl: state.profileAvatarUrl,
            phoneNumber: state.profilePhone,
            contacts: validContacts
          } as any;
        }
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.SUCCESS'),
          detail: this.translate.instant('MESSAGES.PROFILE.UPDATE_SUCCESS')
        });
        state.profileSaving = false;
        this.closeProfileModal(state);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('COMMON.ERROR'),
          detail: this.translate.instant('MESSAGES.PROFILE.UPDATE_ERROR')
        });
        state.profileSaving = false;
      }
    });
  }
}
